import { globalConfirm } from '../components/ConfirmDialog';
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, ActiveBadge, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { SortableTableHeader, useSortState } from '../components/SortableTableHeader';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api, { getApiErrorMessage } from '../services/api';
import ModuleReports from './Reports/ModuleReports';
import { CURRENCIES_REPORTS } from './Reports/reportConfigs';
import { BarChart2, FileText as FileTextIcon } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, RefreshCw } from 'lucide-react';
import DataFilesAnalytics from './DataFilesAnalytics/DataFilesAnalytics';
import { currenciesConfig } from './DataFilesAnalytics/configs';
import { LayoutDashboard as AnalyticsIcon } from 'lucide-react';

const EMPTY = { code: '', description: '', exchange_rate: 1.0, is_base: false, auto_sync: false, active: true };

export default function CurrencyCodesPage() {
  const [records, setRecords] = useState([]);
  const [viewTab, setViewTab] = useState('analytics');
  const { canWrite: _canWrite } = usePermissions();
  const canWriteData = _canWrite('currency_code');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sortState, handleSort] = useSortState('code', 'asc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/data/currencies', {
        params: { sort_by: sortState.field, sort_dir: sortState.dir }
      });
      const _raw = Array.isArray(res.data) ? res.data : (res.data.data || []);
      const _s = _raw.filter(r => r.is_favorite);
      const _u = _raw.filter(r => !r.is_favorite);
      setRecords([..._s, ..._u]);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load currencies'); }
    setLoading(false);
  }, [sortState]);

  useEffect(() => { load(); }, [load]);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const openCreate = () => { setForm(EMPTY); setIsEdit(false); setModalOpen(true); };
  const openEdit   = r  => { setForm({ ...r }); setIsEdit(true); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/data/currencies/${form.code}`, form);
      else        await api.post('/api/data/currencies', form);
      toast.success(isEdit ? 'Updated' : 'Created'); setModalOpen(false); load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteRecord = async () => {
    if (!await globalConfirm('Delete this currency?')) return;
    try { await api.delete(`/api/data/currencies/${form.code}`); toast.success('Deleted'); setModalOpen(false); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Delete failed')); }
  };

  const syncRates = async () => {
    setSyncing(true);
    try { await api.post('/api/data/currencies/sync-rates'); toast.success('Rates synced'); load(); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Sync failed'); }
    setSyncing(false);
  };

  const toggleFav = async (r) => {
    const { toggleFavorite } = await import('../utils/favorites');
    const result = await toggleFavorite('currencies', r.code);
    if (result !== null) {
      setRecords(prev => {
        const updated = prev.map(rec => (rec === r) ? { ...rec, is_favorite: result } : rec);
        const starred = updated.filter(rec => rec.is_favorite);
        const unstarred = updated.filter(rec => !rec.is_favorite);
        return [...starred, ...unstarred];
      });
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Currency Code"
        description={`Total records: ${records.length}`}
        helpGuide="data-files"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline" onClick={syncRates} disabled={syncing} data-testid="currency-sync-button">
            <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync Rates
          </Button>
          <Button size="sm" onClick={openCreate} style={canWriteData ? {} : {display:"none"}} data-testid="currency-add-button">
            <Plus className="h-4 w-4 mr-1" /> Add New
          </Button>
        </> : null}
      />
      <div className="flex gap-4 border-b mb-4" data-testid="currencies-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="currencies-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <AnalyticsIcon className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="currencies-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileTextIcon className="h-4 w-4 inline mr-1.5" /> Currencies
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="currencies-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'analytics' ? (<DataFilesAnalytics {...currenciesConfig} onCardClick={() => setViewTab('list')} />) : viewTab === 'reports' ? (
        <ModuleReports reports={CURRENCIES_REPORTS} apiPrefix="/api/module-reports/data" module="Currencies" />
      ) : (<>

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="py-12"><LoadingSpinner /></div> :
        records.length === 0 ? <EmptyState title="No currencies" action={<Button size="sm" onClick={openCreate} style={canWriteData ? {} : {display:"none"}}>Add</Button>} /> : (
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <SortableTableHeader label="Currency Code" field="code"          sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Description"   field="description"   sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Exchange Rate" field="exchange_rate" sortState={sortState} onSort={handleSort} className="text-right pr-6" />
                <SortableTableHeader label="Auto Sync"    field="auto_sync"      sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Last Updated" field="last_synced"    sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Active"       field="active"         sortState={sortState} onSort={handleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map(r => (
                <TableRow key={r.code} className="cursor-pointer" onClick={() => openEdit(r)}>
                  <TableCell className="font-mono font-semibold text-primary w-[140px]">
                    {r.code}{r.is_base && <span className="text-[10px] text-muted-foreground font-normal ml-1">(Base Currency)</span>}
                  </TableCell>
                  <TableCell className="w-[200px]">{r.description}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums pr-6 w-[130px]">{parseFloat(r.exchange_rate || 1).toFixed(4)}</TableCell>
                  <TableCell>{r.auto_sync ? <Badge variant="secondary" className="text-xs">Auto</Badge> : <span className="text-muted-foreground text-xs">Manual</span>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {r.last_synced ? new Date(r.last_synced).toLocaleString('en-GB', { 
                      day: '2-digit', month: 'short', year: 'numeric', 
                      hour: '2-digit', minute: '2-digit'
                    }) : '—'}
                  </TableCell>
                  <TableCell><ActiveBadge active={r.active} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      </>)}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              {isEdit ? 'Edit Currency' : 'Add Currency'}
              <div className="flex items-center gap-2 mr-6">
                <span className="text-sm font-normal text-muted-foreground">Active</span>
                <Switch checked={form.active} onCheckedChange={v => f('active', v)} />
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Currency Code *</Label>
                <Input value={form.code} onChange={e => f('code', e.target.value.toUpperCase())} placeholder="e.g. USD" disabled={isEdit} maxLength={10} />
              </div>
              <div className="space-y-1.5">
                <Label>Description *</Label>
                <Input value={form.description} onChange={e => f('description', e.target.value)} placeholder="e.g. US Dollar" />
              </div>
              <div className="space-y-1.5">
                <Label>Exchange Rate</Label>
                <Input type="number" step="0.0001" value={form.exchange_rate} onChange={e => f('exchange_rate', parseFloat(e.target.value) || 1)} />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_base} onCheckedChange={v => f('is_base', v)} id="is-base" />
                <Label htmlFor="is-base">Base Currency</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.auto_sync} onCheckedChange={v => f('auto_sync', v)} id="auto-sync" />
                <Label htmlFor="auto-sync">Auto Sync Rate</Label>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            {isEdit && !form.is_base && <Button variant="destructive" onClick={deleteRecord}>Delete</Button>}
            <Button onClick={save} disabled={!canWriteData} disabled={saving || !form.code || !form.description}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
